// Considered: ContextSummaryMolecule — rejected because it is a read-only definition summary
// (levels, filters, segments, settings) for editor/preview use; has no card chrome, source badge,
// owner footer, use-case tags, view count, or click-to-open affordance. See spec Reuse Audit.
import React, { useRef } from 'react';
import { useTranslation } from 'localization';
import type { Context } from 'types/context';
import type { UseCaseRow } from 'types/schema';
import ContextSummaryMolecule, {
  type ResolvedTypeInfo,
} from '../ContextSummary/ContextSummaryMolecule';
import TagAtom from '../../atoms/Tag/TagAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { ContextSource } from '../../../utils/contextClassification';
import { resolveLocalizedText } from '../../../utils/contextClassification';
import './ContextCardMolecule.scss';

function formatRelativeTime(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString(locale);
}

const KEY = 'context.library.card';

function resolveUseCaseMeta(
  systemName: string,
  useCases: UseCaseRow[]
): { label: string; icon?: string } {
  const match = useCases.find((uc) => uc.system_name === systemName);
  return match ? { label: match.name, icon: match.icon || undefined } : { label: systemName };
}

export interface ContextCardMoleculeProps {
  context: Context;
  source: ContextSource;
  appliesTo?: string[];
  useCases?: UseCaseRow[];
  resolvedTypes?: Map<string, ResolvedTypeInfo>;
  locale: string;
  onEdit?: (context: Context) => void;
  onClone?: (context: Context) => void;
  onDelete?: (context: Context) => void;
  className?: string;
  'data-testid'?: string;
}

const ContextCardMolecule: React.FC<ContextCardMoleculeProps> = ({
  context,
  source,
  appliesTo = [],
  useCases = [],
  resolvedTypes,
  locale,
  onEdit,
  onClone,
  onDelete,
  className = '',
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const name = resolveLocalizedText(context.name, locale);
  const description = resolveLocalizedText(context.description, locale);
  const isPlatform = source === 'platform';
  const viewCount = context.viewCount ?? 0;

  const primaryUseCaseTag = context.use_case_tags?.[0];
  const primaryUseCase = primaryUseCaseTag
    ? useCases.find((uc) => uc.system_name === primaryUseCaseTag)
    : null;
  const showVia = isPlatform && !!primaryUseCase;

  const getInitials = (createdBy: string | undefined): string => {
    if (!createdBy) return '?';
    const parts = createdBy.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return createdBy.charAt(0).toUpperCase();
  };

  const handleOverflowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  const handleMenuAction = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    action();
  };

  // Close menu when focus leaves
  const handleMenuBlur = (e: React.FocusEvent) => {
    if (!menuRef.current?.contains(e.relatedTarget as Node)) {
      setMenuOpen(false);
    }
  };

  const sourceBadgeLabel = isPlatform
    ? t(`${KEY}.sourcePlatform`)
    : t(`${KEY}.sourceYours`);

  const summaryContext = {
    systemName: context.system_name,
    name: name,
    definition: context.definition,
    applicability: context.applicability,
    useCaseTags: context.use_case_tags,
  };

  return (
    <div
      className={`context-card-molecule ${className}`}
      data-testid={testId ?? `context-card-${context.system_name}`}
    >
      {/* Header */}
      <div className="context-card-molecule__header">
        <button
          type="button"
          className="context-card-molecule__name"
          onClick={() => onEdit?.(context)}
          data-testid={`context-card-open-${context.system_name}`}
        >
          {name}
        </button>
        <div ref={menuRef} className="context-card-molecule__overflow" onBlur={handleMenuBlur}>
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-ellipsis-h"
            onClick={handleOverflowClick}
            aria-label={t('context.inventory.actions')}
            data-testid={`overflow-trigger-${context.system_name}`}
          />
          {menuOpen && (
            <div className="context-card-molecule__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="context-card-molecule__menu-item"
                onClick={handleMenuAction(() => onEdit?.(context))}
              >
                <i className={isPlatform ? 'pi pi-eye' : 'pi pi-pencil'} aria-hidden="true" />
                {isPlatform ? t('context.inventory.view') : t('context.inventory.edit')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="context-card-molecule__menu-item"
                onClick={handleMenuAction(() => onClone?.(context))}
              >
                <i className="pi pi-copy" aria-hidden="true" />
                {t('context.inventory.clone')}
              </button>
              {!isPlatform && (
                <button
                  type="button"
                  role="menuitem"
                  className="context-card-molecule__menu-item context-card-molecule__menu-item--danger"
                  onClick={handleMenuAction(() => onDelete?.(context))}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                  {t('context.inventory.delete')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Source badge */}
      <div className="context-card-molecule__source" data-testid={`source-badge-${context.system_name}`}>
        {showVia ? (
          <span className="context-card-molecule__source-via">
            <i className="pi pi-circle-fill context-card-molecule__source-via-icon" aria-hidden="true" />
            <span className="context-card-molecule__source-via-prefix">
              {t(`${KEY}.sourceViaPrefix`)}
            </span>
            <span className="context-card-molecule__source-via-name">
              {primaryUseCase!.name}
            </span>
          </span>
        ) : (
          <span className={`context-card-molecule__source-label context-card-molecule__source-label--${isPlatform ? 'platform' : 'yours'}`}>
            <i className={isPlatform ? 'pi pi-lock' : 'pi pi-user'} aria-hidden="true" />
            <span>{isPlatform ? sourceBadgeLabel : sourceBadgeLabel.toUpperCase()}</span>
          </span>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="context-card-molecule__description">{description}</p>
      )}

      {/* Walk-shape signature */}
      <div className="context-card-molecule__walk" data-testid="walk-shape-section">
        <ContextSummaryMolecule
          context={summaryContext}
          resolvedTypes={resolvedTypes}
          mode="compact"
          data-testid={`walk-shape-${context.system_name}`}
        />
      </div>

      {/* Applies-to section */}
      <div className="context-card-molecule__applies-to-wrapper">
        <span className="context-card-molecule__applies-to-label">
          {t(`${KEY}.appliesToLabel`)}
        </span>
        <div className="context-card-molecule__applies-to">
          {appliesTo.length > 0 ? (
            appliesTo.map((type) => (
              <TagAtom key={type} label={type} size="small" tone="neutral" />
            ))
          ) : (
            <TagAtom label={t(`${KEY}.anyType`)} size="small" tone="neutral" />
          )}
        </div>
      </div>

      {/* Use-case tags */}
      {(context.use_case_tags?.length ?? 0) > 0 && (
        <div className="context-card-molecule__use-cases">
          {context.use_case_tags!.map((tag) => {
            const meta = resolveUseCaseMeta(tag, useCases);
            return (
              <TagAtom
                key={tag}
                label={meta.label}
                icon={meta.icon}
                size="small"
                tone="accent"
              />
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="context-card-molecule__footer">
        {isPlatform ? (
          <div className="context-card-molecule__read-only" data-testid="read-only-indicator">
            <i className="pi pi-lock" aria-hidden="true" />
            <span className="context-card-molecule__read-only-text">
              <strong>{t(`${KEY}.readOnly`)}</strong>
              {' — '}
              <em>{t(`${KEY}.readOnlyNote`)}</em>
            </span>
          </div>
        ) : (
          <div className="context-card-molecule__owner" data-testid="owner-section">
            <span className="context-card-molecule__owner-avatar">
              {getInitials(context.created_by)}
            </span>
          </div>
        )}

        <div className="context-card-molecule__meta">
          <span className="context-card-molecule__view-count" data-testid="view-count">
            <i className="pi pi-eye" aria-hidden="true" />
            {t(`${KEY}.views`, { count: viewCount })}
          </span>
          {context.updated_at && (
            <span className="context-card-molecule__updated-at">
              {formatRelativeTime(context.updated_at, locale)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

ContextCardMolecule.displayName = 'ContextCardMolecule';

export default ContextCardMolecule;
